import { useState, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import i18n from '../../i18n/i18n';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Dayjs } from 'dayjs';
import { getDayjsLocale } from '../../helpers/functions/dateLocales';

// MUI imports
import { Box, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Components
import Slot from './Slot';
import SlotPeriodPicker from './SlotPeriodPicker';
import SignEvalTextField from '../SignEvalTextField';

// Helpers
import { useTheme } from '../../hooks/useTheme';
import { useSessionStore } from '../../store/SessionStore';
import { SlotData, SlotDayData, SlotPeriod } from '../../types/slotData';
import { formatDateSlotForApi } from '../../helpers/functions/formatSlot';
import { createSlot } from '../../helpers/api/callSlot';

interface SlotPickerProps {
    slotList: SlotDayData[];
    onSlotChange: (selectedSlot: SlotData) => void;
    onCloseDrawer?: () => void;
    editMode: boolean;
    setSlotsDay?: (slot: SlotDayData) => void;
}

type FormValues = {
    newSlotDate: Dayjs;
};

const SlotPicker = ({ slotList, onSlotChange, editMode, onCloseDrawer, setSlotsDay }: SlotPickerProps) => {
    const theme = useTheme();
    const today = new Date().getDate();
    const todaySlot = slotList.find((slot) => slot.dayNumber === today);
    const slot = todaySlot || slotList[0];
    const { session, reloadSession } = useSessionStore();

    const [selectedSlotDay, setSelectedSlotDay] = useState<SlotDayData>(slot);
    const [period, setPeriod] = useState<SlotPeriod>(SlotPeriod.FIRST_PERIOD);
    const [showAddSlotInput, setShowAddSlotInput] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const selectedSlot = selectedSlotDay[period];

    const settings = {
        className: 'center',
        infinite: false,
        slidesToShow: 6.5,
        swipeToSlide: true,
        accessibility: true,
        speed: 500
    };

    useEffect(() => {
        if (selectedSlot) {
            onSlotChange(selectedSlot);
        }
    }, [selectedSlot, onSlotChange]);

    const {
        handleSubmit,
        control,
        resetField,
        formState: { errors },
        clearErrors,
        getValues,
        watch
    } = useForm<FormValues>();

    const handleHideAddSlotInput = async () => {
        if (showAddSlotInput) {
            setShowAddSlotInput(false);
            resetField('newSlotDate');
            clearErrors('newSlotDate');
            setErrorMessage(null);
            await reloadSession();
            onCloseDrawer?.();
        }
    };

    const handleSlotClick = (slot: SlotDayData) => {
        setSelectedSlotDay(slot);
    };

    const handlePeriodOfDayChange = (newPeriod: SlotPeriod) => {
        setPeriod(newPeriod);
    };

    const handleAddSlotClick = () => {
        if (!showAddSlotInput) {
            setShowAddSlotInput(true);
            resetField('newSlotDate');
            clearErrors('newSlotDate');
        } else if (getValues('newSlotDate')) {
            handleSubmit(onSubmit)();
        } else {
            setShowAddSlotInput(false);
        }
    };

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        const newSlot = formatDateSlotForApi({
            sessionId: session.id,
            newSlotDate: data.newSlotDate
        });

        const periodsToSend = [newSlot[0].firstPeriod, newSlot[0].secondPeriod].filter((period): period is SlotData => !!period);

        try {
            const response = await createSlot(periodsToSend);
            if (response.code == 409) {
                setErrorMessage(i18n.t('UpdateSession.errors.slotsAlreadyExists'));
                return;
            }

            if (response.code !== 200) {
                setErrorMessage(i18n.t('UpdateSession.errors.createSlot'));
                return;
            }

            if (setSlotsDay) {
                setSlotsDay(newSlot[0]);
            }
            handleHideAddSlotInput();
        } catch (error) {
            console.error('Error creating slot:', error);
        }
    };

    const slideSlots = () => {
        return (
            <Slider {...settings}>
                {slotList.map((slot, index) => (
                    <Slot key={index} slot={slot} isSelected={selectedSlotDay.dayNumber === slot.dayNumber} onClick={() => handleSlotClick(slot)} editMode={editMode} />
                ))}
            </Slider>
        );
    };

    if (!editMode) {
        return (
            <>
                <Box sx={{ width: '100%', margin: '1.1em auto 0.3em auto', overflow: 'hidden' }}>{slideSlots()}</Box>
                <SlotPeriodPicker slot={selectedSlotDay} onPeriodOfDayChange={handlePeriodOfDayChange} editMode={editMode} />
            </>
        );
    }

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', width: '85%', margin: '0 auto 0.3em auto' }}>
                    <Box sx={{ width: '100%', margin: '0.5em auto 0.3em auto', overflow: 'hidden' }}>{slideSlots()}</Box>
                    {showAddSlotInput && (
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getDayjsLocale(i18n.language)}>
                            <Controller
                                control={control}
                                name="newSlotDate"
                                defaultValue={undefined}
                                render={({ field }) => (
                                    <DatePicker
                                        label={i18n.t('UpdateSession.inputs.newSlot')}
                                        value={field.value || null}
                                        onChange={(date) => {
                                            field.onChange(date);
                                        }}
                                        slotProps={{
                                            textField: {
                                                InputProps: {
                                                    endAdornment: <EventIcon />,
                                                    sx: { minHeight: '3em', borderRadius: '10px', mb: '0.75em' }
                                                },
                                                inputRef: field.ref,
                                                error: !!errors.newSlotDate
                                            },
                                            toolbar: {
                                                sx: { backgroundColor: theme.palette.text.custom, color: theme.palette.text.secondary }
                                            },
                                            layout: {
                                                sx: {
                                                    '.MuiDayCalendar-weekDayLabel': {
                                                        color: theme.palette.text.custom
                                                    },
                                                    '& .MuiPickersDay-root': {
                                                        '&:focus': {
                                                            backgroundColor: theme.palette.text.custom
                                                        },
                                                        '&:active': {
                                                            backgroundColor: theme.palette.text.custom
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme.palette.text.custom
                                                        },
                                                        '&.MuiPickersDay-daySelected': {
                                                            backgroundColor: theme.palette.text.custom
                                                        }
                                                    },
                                                    color: theme.palette.text.primary,
                                                    backgroundColor: theme.palette.background.default
                                                }
                                            }
                                        }}
                                        localeText={{
                                            toolbarTitle: i18n.t('UpdateSession.inputs.dateNewSlot'),
                                            cancelButtonLabel: i18n.t('CreateSession.button.cancel'),
                                            okButtonLabel: i18n.t('CreateSession.button.ok')
                                        }}
                                        slots={{
                                            textField: SignEvalTextField
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    )}
                </Box>
                <IconButton
                    onClick={() => {
                        handleAddSlotClick();
                    }}
                    sx={{
                        backgroundColor: theme.palette.text.custom,
                        color: theme.palette.text.secondary,
                        borderRadius: '10px',
                        height: showAddSlotInput ? '4.75em' : '2.3em',
                        margin: '0.5em 0 0 0.5em',
                        boxShadow: '0px 3px 7px rgba(0, 0, 0, 0.25)',
                        transition: '0.3s ease all',
                        '&:active': {
                            backgroundColor: theme.palette.text.custom
                        },
                        '&:focus': {
                            backgroundColor: theme.palette.text.custom
                        }
                    }}
                >
                    {!showAddSlotInput ? <AddIcon sx={{ fontSize: '1.1em' }} /> : watch('newSlotDate') ? <DoneOutlinedIcon sx={{ fontSize: '1.1em' }} /> : <CloseIcon sx={{ fontSize: '1.1em' }} />}
                </IconButton>
            </Box>
            {errorMessage && (
                <Typography
                    sx={{
                        color: theme.palette.error.main,
                        fontSize: '0.85em',
                        mb: '0.75em',
                        textAlign: 'center'
                    }}
                >
                    {errorMessage}
                </Typography>
            )}
            <SlotPeriodPicker slot={selectedSlotDay} onPeriodOfDayChange={handlePeriodOfDayChange} editMode={editMode} />
        </>
    );
};

export default SlotPicker;
